export interface ChatMessage {
	id: number;
	content: string;
	role: 'user' | 'bot';
	date_created: string;
}

export interface ChatSession {
	session_secret_hash?: string;
	conversation_id: number;
	full_name: string;
	email: string;
	isAuthenticated?: boolean;
}

export interface ConversationListItem {
	id: number;
	title: string;
	type: string;
	bot_mode: string;
	date_created: string | null;
	date_updated: string | null;
	last_message: {
		content: string;
		date_created: string;
	} | null;
}

export const useChat = () => {
	const { authFetch } = useAuthFetch();
	const session = useState<ChatSession | null>('chat-session', () => null);
	const messages = useState<ChatMessage[]>('chat-messages', () => []);
	const isLoading = useState<boolean>('chat-loading', () => false);
	const isInitializing = useState<boolean>('chat-initializing', () => false);
	const error = useState<string | null>('chat-error', () => null);
	const conversations = useState<ConversationListItem[]>('chat-conversations', () => []);
	const isLoadingConversations = useState<boolean>('chat-loading-conversations', () => false);

	const initializeSession = async (fullName: string, email: string, privacyConsent: boolean) => {
		isInitializing.value = true;
		error.value = null;

		try {
			const response = await $fetch<{
				session_secret_hash: string;
				conversation_id: number;
			}>('/api/conversations/session', {
				method: 'POST',
				body: {
					full_name: fullName,
					email,
					privacy_consent: privacyConsent,
				},
			});

			session.value = {
				session_secret_hash: response.session_secret_hash,
				conversation_id: response.conversation_id,
				full_name: fullName,
				email,
			};

			// Initialize messages as empty array since this is a new conversation
			messages.value = [];

			// Optionally load conversation (but it should be empty for new sessions)
			// await loadConversation(response.session_secret_hash);
		} catch (err: any) {
			error.value = err.data?.message || err.message || 'Errore durante l\'inizializzazione della sessione';
			throw err;
		} finally {
			isInitializing.value = false;
		}
	};

	const loadConversation = async (hashOrId: string | number) => {
		try {
			const { isAuthenticated, accessToken, fetchUser } = useAuth();
			
			// Se hashOrId è un numero, l'utente dovrebbe essere autenticato
			if (typeof hashOrId === 'number') {
				// Se l'utente non è autenticato ma c'è un token nel localStorage, prova a recuperare l'utente
				if (!isAuthenticated.value && process.client) {
					const token = localStorage.getItem('directus_access_token');
					if (token) {
						try {
							await fetchUser();
						} catch (err) {
							// Se fetchUser fallisce, continua con il controllo normale
						}
					}
				}
			}
			
			// Verifica di nuovo dopo il tentativo di fetchUser
			const currentToken = accessToken?.value || (process.client ? localStorage.getItem('directus_access_token') : null);
			
			let response: {
				conversation: any;
				messages: ChatMessage[];
				session: {
					full_name: string;
					email: string;
				};
			};

			if (typeof hashOrId === 'number' && currentToken) {
				// Carica conversazione per utente autenticato usando l'ID
				response = await authFetch(`/api/conversations/${hashOrId}`);
			} else if (typeof hashOrId === 'string') {
				// Carica conversazione per utente guest usando l'hash
				response = await $fetch(`/api/conversations/${hashOrId}`);
			} else {
				throw new Error('Invalid conversation identifier');
			}

			messages.value = response.messages;
			if (session.value) {
				// Aggiorna la sessione esistente con i nuovi dati, incluso conversation_id
				session.value.conversation_id = response.conversation.id;
				session.value.full_name = response.session.full_name;
				session.value.email = response.session.email;
				session.value.isAuthenticated = isAuthenticated.value || !!currentToken;
			} else {
				// Crea una sessione se non esiste
				session.value = {
					conversation_id: response.conversation.id,
					full_name: response.session.full_name,
					email: response.session.email,
					isAuthenticated: isAuthenticated.value || !!currentToken,
				};
			}
		} catch (err: any) {
			error.value = err.data?.message || err.message || 'Errore durante il caricamento della conversazione';
			throw err;
		}
	};

	const loadLastConversation = async () => {
		isInitializing.value = true;
		error.value = null;

		try {
			const { isAuthenticated, accessToken, fetchUser } = useAuth();
			
			// Se l'utente non è autenticato ma c'è un token nel localStorage, prova a recuperare l'utente
			if (!isAuthenticated.value && process.client) {
				const token = localStorage.getItem('directus_access_token');
				if (token) {
					try {
						await fetchUser();
					} catch (err) {
						// Se fetchUser fallisce, continua con il controllo normale
					}
				}
			}
			
			// Verifica di nuovo dopo il tentativo di fetchUser
			const currentToken = accessToken?.value || (process.client ? localStorage.getItem('directus_access_token') : null);
			
			if (!isAuthenticated.value || !currentToken) {
				throw new Error('User not authenticated');
			}

			const response = await authFetch<{
				conversation: {
					id: number;
					title: string;
					type: string;
					bot_mode: string;
				} | null;
				messages: ChatMessage[];
				session: {
					full_name: string;
					email: string;
				};
			}>('/api/conversations/last');

			if (!response.conversation) {
				// Nessuna conversazione esistente
				session.value = null;
				messages.value = [];
				return;
			}

			session.value = {
				conversation_id: response.conversation.id,
				full_name: response.session.full_name,
				email: response.session.email,
				isAuthenticated: true,
			};

			messages.value = response.messages;
		} catch (err: any) {
			error.value = err.data?.message || err.message || 'Errore durante il caricamento della conversazione';
			throw err;
		} finally {
			isInitializing.value = false;
		}
	};

	const loadConversationsList = async () => {
		isLoadingConversations.value = true;
		error.value = null;

		try {
			const { isAuthenticated, accessToken, fetchUser } = useAuth();
			
			// Se l'utente non è autenticato ma c'è un token nel localStorage, prova a recuperare l'utente
			if (!isAuthenticated.value && process.client) {
				const token = localStorage.getItem('directus_access_token');
				if (token) {
					try {
						await fetchUser();
					} catch (err) {
						// Se fetchUser fallisce, continua con il controllo normale
					}
				}
			}
			
			// Verifica di nuovo dopo il tentativo di fetchUser
			const currentToken = accessToken?.value || (process.client ? localStorage.getItem('directus_access_token') : null);
			
			if (!isAuthenticated.value || !currentToken) {
				throw new Error('User not authenticated');
			}

			const response = await authFetch<{
				conversations: ConversationListItem[];
			}>('/api/conversations/list');

			conversations.value = response.conversations;
		} catch (err: any) {
			error.value = err.data?.message || err.message || 'Errore durante il caricamento delle conversazioni';
			throw err;
		} finally {
			isLoadingConversations.value = false;
		}
	};

	const sendMessage = async (content: string, currentPage?: string) => {
		const { getToken } = useAuthFetch();
		const { isAuthenticated, accessToken, user, fetchUser } = useAuth();

		// Se l'utente non è autenticato ma c'è un token nel localStorage, prova a recuperare l'utente
		if (!isAuthenticated.value && process.client) {
			const token = localStorage.getItem('directus_access_token');
			if (token) {
				try {
					await fetchUser();
				} catch (err) {
					// Se fetchUser fallisce, continua con il controllo normale
				}
			}
		}

		// Verifica di nuovo dopo il tentativo di fetchUser
		const currentToken = accessToken?.value || (process.client ? localStorage.getItem('directus_access_token') : null);
		const isUserAuthenticated = isAuthenticated.value || !!currentToken;

		// Se l'utente è autenticato ma non ha ancora una conversazione, creala automaticamente
		if (isUserAuthenticated && currentToken && !session.value) {
			isLoading.value = true;
			error.value = null;

			try {
				const response = await authFetch<{
					conversation_id: number;
					conversation: {
						id: number;
						title: string;
						type: string;
						bot_mode: string;
					};
					session: {
						full_name: string;
						email: string;
					};
				}>('/api/conversations/create', {
					method: 'POST',
				});

				// Crea la sessione con i dati dell'utente autenticato
				session.value = {
					conversation_id: response.conversation_id,
					full_name: response.session.full_name,
					email: response.session.email,
					isAuthenticated: true,
				};

				// Inizializza i messaggi come array vuoto
				messages.value = [];
			} catch (err: any) {
				error.value = err.data?.message || err.message || 'Errore durante la creazione della conversazione';
				isLoading.value = false;
				throw err;
			} finally {
				isLoading.value = false;
			}
		}

		// Se non c'è ancora una sessione dopo il tentativo di creazione, errore
		if (!session.value) {
			throw new Error('Session not initialized');
		}

		// Aggiungi subito il messaggio utente (optimistic update)
		const userMessage: ChatMessage = {
			id: Date.now(),
			content,
			role: 'user',
			date_created: new Date().toISOString(),
		};
		messages.value.push(userMessage);

		// Crea un messaggio bot "vuoto" che verrà popolato via streaming
		const botMessage: ChatMessage = {
			id: Date.now() + 1,
			content: '',
			role: 'bot',
			date_created: new Date().toISOString(),
		};
		messages.value.push(botMessage);

		isLoading.value = true;
		error.value = null;

		try {
			const requestBody: any = {
				content,
				current_page: currentPage,
			};

			// Se la sessione ha conversation_id, è una sessione autenticata
			// Altrimenti, se ha session_secret_hash, è una sessione guest
			if (session.value.conversation_id && currentToken) {
				requestBody.conversation_id = session.value.conversation_id;
			} else if (session.value.session_secret_hash) {
				requestBody.session_secret_hash = session.value.session_secret_hash;
			} else {
				throw new Error('Session not properly initialized');
			}

			const headers: Record<string, string> = {
				'Content-Type': 'application/json',
			};
			// Se è una sessione autenticata, aggiungi l'header Authorization (getToken() restituisce il token eventualmente rinnovato)
			const tokenForRequest = getToken();
			if (session.value.conversation_id && tokenForRequest) {
				headers.Authorization = `Bearer ${tokenForRequest}`;
			}

			// Usa fetch "normale" per poter leggere lo stream di risposta
			const res = await fetch('/api/conversations/message', {
				method: 'POST',
				headers,
				body: JSON.stringify(requestBody),
			});

			if (!res.body) {
				throw new Error('Nessuno stream di risposta dal server');
			}

			const reader = res.body.getReader();
			const decoder = new TextDecoder('utf-8');
			let done = false;

			while (!done) {
				const { value, done: doneReading } = await reader.read();
				done = doneReading;
				if (!value) continue;

				const chunk = decoder.decode(value, { stream: true });

				// Gestisce lo stream SSE in formato OpenAI (linee "data: {...}")
				for (const line of chunk.split('\n')) {
					if (!line.startsWith('data:')) continue;
					const data = line.replace(/^data:\s*/, '');
					if (data === '[DONE]') {
						done = true;
						break;
					}
					try {
						const parsed = JSON.parse(data);
						const delta = parsed.choices?.[0]?.delta?.content || '';
						if (delta) {
							// Aggiorna il messaggio del bot
							botMessage.content += delta;
							// Forza il refresh della lista per garantire il re-render della UI
							messages.value = [...messages.value];
						}
					} catch {
						// Ignora righe non parse‑abili
					}
				}
			}
		} catch (err: any) {
			// In caso di errore rimuovi il messaggio bot "vuoto"
			messages.value = messages.value.filter(msg => msg.id !== botMessage.id);
			error.value = err.data?.message || err.message || 'Errore durante l\'invio del messaggio';
			throw err;
		} finally {
			isLoading.value = false;
		}
	};

	const createNewConversation = async () => {
		isInitializing.value = true;
		error.value = null;

		try {
			const { isAuthenticated, accessToken, fetchUser } = useAuth();
			
			// Se l'utente non è autenticato ma c'è un token nel localStorage, prova a recuperare l'utente
			if (!isAuthenticated.value && process.client) {
				const token = localStorage.getItem('directus_access_token');
				if (token) {
					try {
						await fetchUser();
					} catch (err) {
						// Se fetchUser fallisce, continua con il controllo normale
					}
				}
			}
			
			// Verifica di nuovo dopo il tentativo di fetchUser
			const currentToken = accessToken?.value || (process.client ? localStorage.getItem('directus_access_token') : null);
			
			if (!isAuthenticated.value || !currentToken) {
				throw new Error('User not authenticated');
			}

			const response = await authFetch<{
				conversation_id: number;
				conversation: {
					id: number;
					title: string;
					type: string;
					bot_mode: string;
				};
				session: {
					full_name: string;
					email: string;
				};
			}>('/api/conversations/create', {
				method: 'POST',
			});

			// Crea la sessione con i dati dell'utente autenticato
			session.value = {
				conversation_id: response.conversation_id,
				full_name: response.session.full_name,
				email: response.session.email,
				isAuthenticated: true,
			};

			// Inizializza i messaggi come array vuoto
			messages.value = [];

			// Ricarica la lista delle conversazioni per includere la nuova
			await loadConversationsList();
		} catch (err: any) {
			error.value = err.data?.message || err.message || 'Errore durante la creazione della conversazione';
			throw err;
		} finally {
			isInitializing.value = false;
		}
	};

	const resetChat = () => {
		session.value = null;
		messages.value = [];
		error.value = null;
		isLoading.value = false;
		isInitializing.value = false;
		conversations.value = [];
	};

	return {
		session: readonly(session),
		messages: readonly(messages),
		isLoading: readonly(isLoading),
		isInitializing: readonly(isInitializing),
		error: readonly(error),
		conversations: readonly(conversations),
		isLoadingConversations: readonly(isLoadingConversations),
		initializeSession,
		loadConversation,
		loadLastConversation,
		loadConversationsList,
		createNewConversation,
		sendMessage,
		resetChat,
	};
};

